import React, { useState, useEffect, useRef } from 'react';

interface EditableTitleProps {
  title: string;
  onUpdate: (newTitle: string) => void;
  className?: string;
  placeholder?: string;
  isEditing?: boolean;
  onInputChange?: (value: string) => void;
}

export function EditableTitle({
  title,
  onUpdate,
  className = "text-2xl font-bold text-gray-900 dark:text-white",
  placeholder = "Введите заголовок...",
  isEditing = false,
  onInputChange
}: EditableTitleProps) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [inputValue, setInputValue] = useState(title);
  const [showPlaceholder, setShowPlaceholder] = useState(!title);
  const inputRef = useRef<HTMLSpanElement>(null);
  const caretPositionRef = useRef<number | null>(null);

  // Инициализация режима редактирования если передан props isEditing
  useEffect(() => {
    if (isEditing) {
      setIsEditingTitle(true);
    }
  }, [isEditing]);

  // Устанавливаем значение при изменении title из props
  useEffect(() => {
    setInputValue(title);
    setShowPlaceholder(!title);
  }, [title]);

  // Установка фокуса на элемент после активации режима редактирования
  useEffect(() => {
    if (isEditingTitle && inputRef.current) {
      inputRef.current.focus();
      
      // Исправление проблемы с курсором - ставим курсор в конец текста
      const selection = window.getSelection();
      const range = document.createRange();
      
      if (selection && inputRef.current.childNodes.length > 0) {
        range.selectNodeContents(inputRef.current);
        range.collapse(false); // Переместить курсор в конец
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }, [isEditingTitle]);

  // Этот useEffect будет следить за изменениями caretPositionRef и восстанавливать позицию курсора
  useEffect(() => {
    if (isEditingTitle && inputRef.current && caretPositionRef.current !== null) {
      try {
        // Восстанавливаем положение курсора
        const selection = window.getSelection();
        const range = document.createRange();
        
        if (selection && inputRef.current.childNodes.length > 0) {
          // Если есть только текстовый узел
          if (inputRef.current.childNodes.length === 1 && inputRef.current.firstChild?.nodeType === Node.TEXT_NODE) {
            const textNode = inputRef.current.firstChild;
            const position = Math.min(caretPositionRef.current, textNode.textContent?.length || 0);
            range.setStart(textNode, position);
            range.setEnd(textNode, position);
          } else {
            // Если структура сложнее, просто ставим в конец
            range.selectNodeContents(inputRef.current);
            range.collapse(false);
          }
          
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } catch (error) {
        console.error('Ошибка при восстановлении положения курсора:', error);
      }
    }
  }, [inputValue, isEditingTitle]);

  const handleClick = () => {
    if (!isEditingTitle) {
      setIsEditingTitle(true);
    }
  };

  const handleBlur = () => {
    // Если поле пустое при потере фокуса, не завершаем редактирование
    if (!inputValue.trim()) {
      console.log('EditableTitle: Поле пустое при потере фокуса, редактирование не завершается');
      setShowPlaceholder(true);
      return;
    }
    finishEditing();
  };



    const handleInput = (e: React.FormEvent<HTMLSpanElement>) => {
    // Сохраняем текущую позицию курсора перед обновлением значения
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      caretPositionRef.current = range.startOffset;
    }

    const newValue = e.currentTarget.textContent || '';
    setInputValue(newValue);

    // Управляем показом placeholder
    setShowPlaceholder(!newValue.trim());

    // Вызываем колбэк для мгновенного обновления в меню
    if (onInputChange) {
      onInputChange(newValue);
    }
  };

  // Защищаем от потери поля редактирования при полном очищении
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Если поле пустое, не завершаем редактирование
      if (!inputValue.trim()) {
        console.log('EditableTitle: Поле пустое, редактирование не завершается');
        return;
      }
      finishEditing();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Отмена редактирования - возвращаем исходное значение
      setInputValue(title);
      setShowPlaceholder(!title);
      setIsEditingTitle(false);
    }
  };

  const finishEditing = () => {
    setIsEditingTitle(false);
    caretPositionRef.current = null;

    // Если пользователь полностью очистил поле, устанавливаем минимальное значение для сохранения
    const finalValue = inputValue.trim();
    const valueToSave = finalValue || (title || placeholder);

    // Всегда вызываем onUpdate при завершении редактирования
    onUpdate(valueToSave);

    // Добавляем логи для отладки
    console.log('EditableTitle: finishEditing завершено', {
      вводимоеЗначение: inputValue,
      исходныйЗаголовок: title,
      сохраняемоеЗначение: valueToSave
    });
  };

  return (
    <div
      className={`relative cursor-text ${className}`}
      onClick={handleClick}
    >
      {isEditingTitle ? (
        <span
          ref={inputRef}
          contentEditable
          suppressContentEditableWarning
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          className="outline-none w-full block"
          style={{
            minWidth: '1px'
          }}
          data-placeholder={!inputValue.trim() ? 'Введите название...' : undefined}
        >
          {inputValue}
        </span>
      ) : (
        <span>{title || placeholder}</span>
      )}

      {/* Добавляем CSS для placeholder в contentEditable */}
      <style dangerouslySetInnerHTML={{
        __html: `
          [data-placeholder]:empty::before {
            content: attr(data-placeholder);
            color: #6b7280;
            font-style: italic;
            pointer-events: none;
          }
        `
      }} />
    </div>
  );
} 